import React, { Component } from "react";
import { connect } from "react-redux";
import { PageHeader, Select, Spin, Modal, message } from "antd";
import { PlusOutlined, EnvironmentOutlined, CalendarOutlined, ReloadOutlined, CarOutlined, DashboardOutlined, TeamOutlined, CoffeeOutlined, BarChartOutlined } from "@ant-design/icons";
import moment from "moment";
import { ButtonItem, SegmentedItem } from "../../../../Components/Items";
import CalendarioCMMS, { LeyendaCMMS } from "../componentes/CalendarioCMMS";
import FiltroIngenieroCMMS, { esperarFiltroDefault } from "../componentes/FiltroIngenieroCMMS";
import ModalViajeCMMS from "./ModalViajeCMMS";
import ModalListasViajesCMMS from "./ModalListasViajesCMMS";
import ResumenViajesCMMS from "./ResumenViajesCMMS";
import { cmmsPost, fechaDiaPayload } from "../cmmsComun";
import "../cmms.css";

const { Option } = Select;

// Viajes de los ingenieros (C-LO-01 "Calendario de viajes"): cada viaje es un renglon con
// municipio, unidad, vehiculo y personal; en amarillo los que cancelan comedor. Se arrastra para
// cambiar de dia (conserva los dias que dura). Clic en un dia vacio = nuevo viaje ese dia.

const COLOR_VIAJE = "#2fb8d6";
const COLOR_COMEDOR = "#e0b000";
const LEYENDA = [
    { label: "Viaje", hex: COLOR_VIAJE },
    { label: "Cancela comedor", hex: COLOR_COMEDOR, ayuda: "Esos días no comen en la oficina" },
    { label: "Día festivo", rayas: true, ayuda: "Se configuran en Días festivos" },
];

const VISTAS_PAGINA = [
    {
        value: "calendario",
        label: (
            <span>
                <CalendarOutlined /> Calendario
            </span>
        ),
    },
    {
        value: "resumen",
        label: (
            <span>
                <BarChartOutlined /> Resumen semanal
            </span>
        ),
    },
];

const dia = (f) => moment(String(f).slice(0, 10), "YYYY-MM-DD");
const plural = (n, uno, varios) => `${n === 1 ? uno : varios}`;

// Un indicador del resumen del periodo (arriba del calendario).
const Indicador = ({ icono, valor, texto, color }) => (
    <span className="cmms-viajes-stat">
        <span className="cmms-viajes-stat-icono" style={color ? { color, background: `${color}1f` } : undefined}>
            {icono}
        </span>
        <span className="cmms-viajes-stat-valor">{valor}</span>
        <span className="cmms-viajes-stat-texto">{texto}</span>
    </span>
);

class Viajes extends Component {
    state = {
        cargando: false,
        rango: null,
        empleadoId: null,
        vehiculoId: null,
        viajes: [],
        festivos: [],
        opciones: { vehiculos: [], destinos: [], festivos: [] },
        modal: null, // { viajeId } | { fecha }
        lista: null, // "destinos" | "festivos"
        pestana: "calendario",
        recargaResumen: 0,
    };

    componentDidMount() {
        this.cargarOpciones();
    }

    cargarOpciones = async () => {
        try {
            const res = await cmmsPost(this.props, "opcionesViajesCMMS");
            if (res.data.success === 2) this.setState({ opciones: { vehiculos: res.data.vehiculos, destinos: res.data.destinos, festivos: res.data.festivos } });
        } catch (error) {
            console.log(error);
        }
    };

    consultar = async (rango = this.state.rango) => {
        if (!rango) return;
        this.setState({ cargando: true, rango });
        try {
            // La primera vez arranca con el ingeniero que fijó el usuario.
            await esperarFiltroDefault(this);
            const res = await cmmsPost(this.props, "viajesCMMS", {
                desde: fechaDiaPayload(rango.desde),
                hasta: fechaDiaPayload(rango.hasta),
                empleadoId: this.state.empleadoId,
                vehiculoId: this.state.vehiculoId,
            });
            if (res.data.success === 2) this.setState({ viajes: res.data.viajes, festivos: res.data.festivos });
            else message.error(res.data.message || "No se pudieron consultar los viajes");
        } catch (error) {
            console.log(error);
            message.error("No se pudieron consultar los viajes");
        } finally {
            this.setState({ cargando: false });
        }
    };

    filtrar = (cambios) => this.setState(cambios, () => this.consultar());

    // Viajes (los dias festivos se pintan en el dia, no como evento).
    eventos = () =>
        this.state.viajes.map((v) => ({
            id: v._id,
            tipo: "viaje",
            viaje: v,
            title: v.destino || v.unidadesNombre || "Viaje",
            subtitulo: [v.destino ? v.unidadesNombre : "", v.vehiculoNombre, v.empleadosNombre].filter(Boolean).join(" · "),
            detalle: [v.codigo, v.km ? `${v.km.toLocaleString("es-MX")} km` : "", v.cancelaComedor ? "Cancela comedor" : "", v.notas].filter(Boolean).join(" · "),
            start: dia(v.fechaInicio).toDate(),
            // Eventos de todo el dia: el fin es exclusivo (el dia siguiente al regreso).
            end: dia(v.fechaFin).add(1, "day").toDate(),
            color: v.cancelaComedor ? COLOR_COMEDOR : COLOR_VIAJE,
            arrastrable: true,
        }));

    // Periodo del que se resume: el mes que se ve (no los dias de los meses vecinos), o la semana /
    // los dias de la lista.
    periodo = () => {
        const r = this.state.rango;
        if (!r) return null;
        if (r.vista === "month" && r.fecha) return { desde: r.fecha.clone().startOf("month"), hasta: r.fecha.clone().endOf("month"), etiqueta: `en ${r.fecha.format("MMMM YYYY")}` };
        return { desde: r.desde, hasta: r.hasta, etiqueta: r.vista === "week" ? "en la semana" : "en los días de la lista" };
    };

    // Como el resumen del C-LO-01: cuenta los viajes por el dia en que salen.
    resumenPeriodo = () => {
        const p = this.periodo();
        if (!p) return null;
        const salen = this.state.viajes.filter((v) => {
            const d = dia(v.fechaInicio);
            return !d.isBefore(p.desde, "day") && !d.isAfter(p.hasta, "day");
        });
        const suma = (lista, campo) => lista.reduce((s, v) => s + (Number(v[campo]) || 0), 0);
        const comedor = suma(
            salen.filter((v) => v.cancelaComedor),
            "dias"
        );
        const ingenieros = new Set(salen.flatMap((v) => (v.empleados || []).map((e) => String(e._id)))).size;
        return (
            <div className="cmms-viajes-stats">
                <Indicador icono={<CarOutlined />} valor={salen.length} texto={plural(salen.length, "viaje", "viajes")} />
                <Indicador icono={<CalendarOutlined />} valor={suma(salen, "dias")} texto="días fuera" />
                <Indicador icono={<DashboardOutlined />} valor={suma(salen, "km").toLocaleString("es-MX")} texto="km" />
                <Indicador icono={<TeamOutlined />} valor={ingenieros} texto={plural(ingenieros, "ingeniero", "ingenieros")} />
                {comedor ? <Indicador icono={<CoffeeOutlined />} valor={comedor} texto={plural(comedor, "día sin comedor", "días sin comedor")} color={COLOR_COMEDOR} /> : null}
                <span className="cmms-viajes-stats-nota">Viajes que salen {p.etiqueta}</span>
            </div>
        );
    };

    mover = async (evento, fecha, confirmar = false) => {
        const res = await cmmsPost(this.props, "moverViajeCMMS", { id: evento.viaje._id, fechaInicio: fechaDiaPayload(fecha), confirmar });
        if (res.data.success === 3)
            return Modal.confirm({
                title: "Se cruza con otros viajes",
                content: <div style={{ whiteSpace: "pre-wrap" }}>{res.data.message}</div>,
                okText: "Moverlo de todos modos",
                cancelText: "Regresar",
                onOk: () => this.mover(evento, fecha, true),
            });
        if (res.data.success === 2) this.consultar();
        else message.error(res.data.message || "No se pudo mover el viaje");
    };

    cerrarModal = (cambio) =>
        this.setState({ modal: null }, () => {
            if (!cambio) return;
            this.consultar();
            this.cargarOpciones();
            this.setState((s) => ({ recargaResumen: s.recargaResumen + 1 }));
        });

    render() {
        const { opciones } = this.state;
        return (
            <div className="cmms-pagina">
                <PageHeader
                    title="Viajes"
                    subTitle="Calendario de viajes de los ingenieros"
                    style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
                    extra={
                        <div className="cmms-acciones">
                            <ButtonItem blanco botonAncho lessTime icon={<EnvironmentOutlined />} onClick={() => this.setState({ lista: "destinos" })}>
                                Destinos
                            </ButtonItem>
                            <ButtonItem blanco botonAncho lessTime icon={<CalendarOutlined />} onClick={() => this.setState({ lista: "festivos" })}>
                                Días festivos
                            </ButtonItem>
                            <ButtonItem blanco botonAncho icon={<ReloadOutlined />} onClick={() => this.consultar()}>
                                Actualizar
                            </ButtonItem>
                            <ButtonItem cyan botonAncho lessTime icon={<PlusOutlined />} onClick={() => this.setState({ modal: { fecha: moment() } })}>
                                Nuevo viaje
                            </ButtonItem>
                        </div>
                    }
                />
                <div className="cmms-viajes-barra">
                    <div className="cmms-viajes-filtros">
                        <div className="cmms-viajes-filtro">
                            <span className="cmms-viajes-filtro-etiqueta">Ingeniero</span>
                            <FiltroIngenieroCMMS {...this.props} width={280} value={this.state.empleadoId} onChange={(empleadoId) => this.filtrar({ empleadoId })} />
                        </div>
                        <div className="cmms-viajes-filtro">
                            <span className="cmms-viajes-filtro-etiqueta">Vehículo</span>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                placeholder="Todos los vehículos"
                                style={{ width: 240 }}
                                value={this.state.vehiculoId || undefined}
                                onChange={(vehiculoId) => this.filtrar({ vehiculoId: vehiculoId || null })}
                            >
                                {opciones.vehiculos.map((v) => (
                                    <Option key={v._id} value={v._id}>
                                        {v.nombre}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                    <SegmentedItem options={VISTAS_PAGINA} value={this.state.pestana} onChange={(pestana) => this.setState({ pestana })} />
                </div>
                {/* El calendario se queda montado (conserva mes y vista al ir y volver del resumen). */}
                <div style={{ display: this.state.pestana === "calendario" ? "block" : "none" }}>
                    <Spin spinning={this.state.cargando}>
                        <CalendarioCMMS
                            events={this.eventos()}
                            festivos={this.state.festivos}
                            alto={740}
                            onRango={(r) => this.consultar(r)}
                            onMover={this.mover}
                            onDiaClick={(fecha) => this.setState({ modal: { fecha } })}
                            textoNuevo="+ Viaje"
                            nombreEvento="Viaje"
                            onSeleccionar={(e) => this.setState({ modal: { viajeId: e.viaje._id } })}
                            leyenda={<LeyendaCMMS items={LEYENDA} />}
                            ayuda="Clic en un día para registrar un viaje; arrastre un viaje para cambiarlo de día."
                            extra={this.resumenPeriodo()}
                        />
                    </Spin>
                </div>
                {this.state.pestana === "resumen" && <ResumenViajesCMMS {...this.props} empleadoId={this.state.empleadoId} recarga={this.state.recargaResumen} />}

                <ModalViajeCMMS
                    visible={!!this.state.modal}
                    viajeId={this.state.modal?.viajeId || null}
                    fechaInicial={this.state.modal?.fecha}
                    empleadoInicial={this.state.empleadoId}
                    opciones={opciones}
                    onClose={this.cerrarModal}
                />
                <ModalListasViajesCMMS
                    {...this.props}
                    visible={!!this.state.lista}
                    tipo={this.state.lista}
                    valores={this.state.lista === "festivos" ? opciones.festivos : opciones.destinos}
                    onClose={(cambio) =>
                        this.setState({ lista: null }, () => {
                            if (!cambio) return;
                            this.cargarOpciones();
                            this.consultar();
                        })
                    }
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(Viajes);
